import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Tag } from "@impero-com/ui";
import { Popover } from "antd";

import { listModeCategories } from "./baseTagList.css";
import * as theme from "./theme.css";

type TagListMode = "HOVER" | "LIST";

interface BaseTagListProps<TAG, CATEGORY> {
    mode: TagListMode;

    categories: Array<CATEGORY>;
    getTags: (category: CATEGORY) => Array<TAG>;

    getCategoryLabel: (category: CATEGORY) => string;
    getCategoryKey: (category: CATEGORY) => string;

    getTagLabel: (tag: TAG) => string;
    getTagKey: (category: CATEGORY, tag: TAG) => string;
}

class BaseTagList<TAG, CATEGORY> extends React.Component<
    BaseTagListProps<TAG, CATEGORY>
> {
    render(): React.ReactNode {
        return <div className={theme.tagsContainer}>{this.renderTags()}</div>;
    }
    private renderTags(): React.ReactNode {
        switch (this.props.mode) {
            case "HOVER":
                return this.renderHoverMode();
            case "LIST":
                return this.renderListMode();
            default:
                return assertNever(this.props.mode);
        }
    }

    private renderListMode = (): React.ReactNode => {
        const {
            categories,
            getTags,
            getCategoryLabel,
            getCategoryKey,
            getTagLabel,
        } = this.props;

        const tagElements = categories.map((category) => {
            const joinedTags = getTags(category)
                .map((t) => getTagLabel(t))
                .join(", ");
            return (
                <Tag
                    wrap
                    className={theme.listModeCategory}
                    key={getCategoryKey(category)}
                >
                    <b>{getCategoryLabel(category)}</b>: {joinedTags}
                </Tag>
            );
        });

        return <div className={listModeCategories}>{tagElements}</div>;
    };

    private renderHoverMode = (): React.ReactNode => {
        const { categories, getTags, getCategoryLabel, getCategoryKey } =
            this.props;

        return (
            <>
                {categories.map((category) => {
                    const tags = getTags(category);
                    return (
                        <Popover
                            key={"popover-" + getCategoryKey(category)}
                            content={this.popupContent(category, tags)}
                        >
                            <Tag wrap className={theme.tagValue}>
                                <b>{getCategoryLabel(category)}</b> (
                                {tags.length})
                            </Tag>
                        </Popover>
                    );
                })}
            </>
        );
    };

    private popupContent = (category: CATEGORY, tags: Array<TAG>) => {
        const { getTagKey, getTagLabel } = this.props;

        return (
            <div className={theme.tagPopUpContainer}>
                {tags.map((tag) => (
                    <Tag
                        wrap
                        key={getTagKey(category, tag)}
                        className={theme.tagListTag}
                    >
                        {getTagLabel(tag)}
                    </Tag>
                ))}
            </div>
        );
    };
}

export { type BaseTagListProps, type TagListMode, BaseTagList };
